import { Reveal } from "../Reveal";
import { useLang } from "@/i18n";

const Section = ({ title, children }) => (
  <Reveal>
    <div className="border-t border-[#1A1B18]/10 pt-8">
      <h2 className="font-modern text-xl md:text-2xl font-medium tracking-tight mb-4">{title}</h2>
      <div className="text-sm md:text-base text-[#1A1B18]/70 leading-relaxed space-y-3">{children}</div>
    </div>
  </Reveal>
);

export default function Privacy() {
  const { lang } = useLang();
  return (
    <div className="v2-modern bg-[#FAF9F6] text-[#1A1B18] min-h-screen">
      <header className="border-b border-[#1A1B18]/10">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <a href="/" data-testid="privacy-logo" className="font-modern text-xl md:text-2xl font-semibold tracking-tight">
            Stylottica Ceglie
          </a>
          <a
            href="/"
            data-testid="privacy-back"
            className="rounded-full border border-[#1A1B18]/30 text-[10px] uppercase tracking-[0.15em] px-4 py-2 hover:bg-[#1A1B18] hover:text-white transition-colors duration-300"
          >
            {lang === "it" ? "Torna al sito" : "Back to site"}
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16 md:py-24">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#A86946] mb-4">Privacy</p>
          <h1 className="font-modern text-4xl md:text-6xl font-medium tracking-tighter leading-[1.05] mb-6">
            Informativa sulla privacy
          </h1>
          <p className="text-sm text-[#1A1B18]/50 mb-14">
            Ultimo aggiornamento: agosto 2026 · ai sensi del Regolamento (UE) 2016/679 ("GDPR")
          </p>
        </Reveal>

        <div className="space-y-10">
          <Section title="1. Titolare del trattamento">
            <p>
              Il titolare del trattamento dei dati personali è <strong>Stylottica S.A.S.</strong> (insegna <strong>Stylottica Ceglie</strong>), con sede
              in Via Cristoforo Colombo 40, 72013 Ceglie Messapica (BR), P.IVA 02325720742.
            </p>
            <p>
              Per qualsiasi richiesta puoi scriverci a{" "}
              <a href="mailto:infostylottica@libero.it" className="text-[#A86946] underline underline-offset-2">
                infostylottica@libero.it
              </a>{" "}
              o chiamare lo <a href="tel:08311981780" className="text-[#A86946] underline underline-offset-2">0831 19 81 780</a>.
            </p>
          </Section>

          <Section title="2. Quali dati raccogliamo e perché">
            <p>Raccogliamo solo i dati necessari a risponderti e gestire le tue richieste:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong>Modulo di contatto:</strong> nome, telefono e messaggio, per ricontattarti.
              </li>
              <li>
                <strong>Ordini online:</strong> nome, telefono e contenuto del carrello, per preparare il tuo ordine e
                accordarci su ritiro o consegna. Il pagamento avviene in negozio o tramite WhatsApp: non trattiamo dati
                di carte di credito.
              </li>
              <li>
                <strong>Prenotazione visite:</strong> nome, telefono, servizio scelto, giorno e orario, per gestire il
                tuo appuntamento.
              </li>
              <li>
                <strong>Prova virtuale:</strong> la foto che scatti o carichi viene elaborata in tempo reale per
                mostrarti gli occhiali sul tuo volto e <strong>non viene mai salvata</strong> né condivisa: al termine
                dell'elaborazione viene eliminata.
              </li>
            </ul>
            <p>Non raccogliamo dati di navigazione a fini di profilazione e non vendiamo mai i tuoi dati a terzi.</p>
          </Section>

          <Section title="3. Base giuridica e conservazione">
            <p>
              Trattiamo i dati per eseguire le misure richieste (risposta, ordine, appuntamento) e per i nostri legittimi
              interessi organizzativi. I dati di contatto, ordini e appuntamenti sono conservati per il tempo necessario
              a gestire il rapporto e gli obblighi di legge, e comunque non oltre 24 mesi dall'ultimo contatto, salvo
              diversi obblighi fiscali.
            </p>
          </Section>

          <Section title="4. Servizi di terze parti">
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong>WhatsApp (Meta Platforms):</strong> i pulsanti "ordina su WhatsApp" aprono l'app o il sito di
                WhatsApp, soggetti alla propria informativa privacy.
              </li>
              <li>
                <strong>Google Maps:</strong> la mappa interattiva nella sezione contatti è fornita da Google LLC, che
                può installare cookie tecnici e di misurazione secondo la propria informativa.
              </li>
              <li>
                <strong>Intelligenza artificiale (Google Gemini):</strong> la foto della prova virtuale viene trasmessa
                in modo sicuro al servizio AI solo per generare l'anteprima e non viene conservata.
              </li>
              <li>
                <strong>Hosting:</strong> il sito è ospitato su infrastruttura cloud europea con database cifrato.
              </li>
            </ul>
          </Section>

          <Section title="5. Cookie">
            <p>
              Questo sito <strong>non usa cookie di profilazione né analytics</strong>. Sono presenti esclusivamente
              cookie tecnici necessari al funzionamento (es. area riservata del titolare) e gli eventuali cookie della
              mappa Google incorporata. Per questo non è richiesto alcun banner cookie.
            </p>
          </Section>

          <Section title="6. I tuoi diritti">
            <p>
              In qualunque momento puoi chiedere l'accesso, la rettifica, la cancellazione, la limitazione o la
              portabilità dei tuoi dati, e opporti al trattamento, scrivendo a{" "}
              <a href="mailto:infostylottica@libero.it" className="text-[#A86946] underline underline-offset-2">
                infostylottica@libero.it
              </a>
              . Hai inoltre diritto di reclamo al Garante per la protezione dei dati personali (
              <a
                href="https://www.garanteprivacy.it"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#A86946] underline underline-offset-2"
              >
                garanteprivacy.it
              </a>
              ).
            </p>
          </Section>

          <Section title="7. Modifiche a questa informativa">
            <p>
              Eventuali aggiornamenti saranno pubblicati su questa pagina con la data di revisione. Ti invitiamo a
              consultarla periodicamente.
            </p>
          </Section>
        </div>
      </main>

      <footer className="border-t border-[#1A1B18]/10">
        <div className="max-w-4xl mx-auto px-6 py-8 text-xs text-[#1A1B18]/40">
          © {new Date().getFullYear()} Stylottica Ceglie · Stylottica S.A.S. · Ceglie Messapica
        </div>
      </footer>
    </div>
  );
}
